// ═══════════════════════════════════════════════════════════════════════════
//  PINE LIBRARY — persistent multi-script store (localStorage + built-ins)
//  Fixes "only one bot / multi-code save not working":
//   • Every script (built-in or user) lives in one merged library.
//   • Edits autosave to localStorage, so switching scripts never loses code.
//   • User can create, rename, duplicate and delete unlimited bots.
//
//  v3 (2026-07-11) — SYMBOL-WISE scripts:
//   • Each script may carry `symbols: string[]` (canonical apiSym form, e.g.
//     BANK_NIFTY). Empty/absent = GENERIC (works on every symbol — the old
//     behaviour, and what every migrated v2 script becomes unless its code
//     already carried a `//@symbols` directive).
//   • The editor keys its "active script" per chart symbol, so switching the
//     chart between symbols preserves each symbol's editing state.
// ═══════════════════════════════════════════════════════════════════════════
import { PINE_SCRIPTS } from './scripts';

export interface PineScript {
  id: string;
  title: string;
  code: string;
  builtin: boolean;     // true = ships with the app (can be overridden, not deleted)
  updatedAt: number;
  /** Canonical symbols this script is written for; empty/undefined = generic. */
  symbols?: string[];
}

const LS_KEY_V2 = 'jc_pine_lib_v2';
const LS_KEY = 'jc_pine_lib_v3';

/** User overrides + user-created scripts, keyed by id. */
type UserStore = Record<string, {
  title: string; code: string; updatedAt: number; symbols?: string[]; deleted?: boolean;
}>;

/** "BANK NIFTY" / "bank nifty" → "BANK_NIFTY" (matches botversions/registry keys). */
export function canonSym(s: string | null | undefined): string {
  return String(s || '').trim().toUpperCase().replace(/\s+/g, '_');
}

/** Extract a `//@symbol X` / `//@symbols X, Y` directive from Pine source. */
export function parseSymbolDirective(code: string): string[] | undefined {
  const m = String(code || '').match(/\/\/\s*@symbols?[:=\s]+([A-Za-z0-9_ ,]+)/);
  if (!m) return undefined;
  const syms = m[1].split(/[\s,]+/).filter(Boolean).map(canonSym);
  return syms.length ? syms : undefined;
}

function readUserStore(): UserStore {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return JSON.parse(raw) as UserStore;
    // one-time lossless migration from v2 — v2 is left in place as a backup.
    const v2raw = localStorage.getItem(LS_KEY_V2);
    if (!v2raw) return {};
    const v2 = JSON.parse(v2raw) as UserStore;
    const v3: UserStore = {};
    for (const [id, u] of Object.entries(v2)) {
      v3[id] = { ...u, symbols: u.deleted ? undefined : parseSymbolDirective(u.code) };
    }
    writeUserStore(v3);
    return v3;
  } catch {
    return {};
  }
}

function writeUserStore(store: UserStore): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(store));
  } catch {
    /* quota / private-mode — fail silently, in-memory state still works */
  }
}

/** Built-in scripts as a base list (id = the PINE_SCRIPTS key). */
function builtinList(): PineScript[] {
  return Object.entries(PINE_SCRIPTS).map(([id, v]) => ({
    id,
    title: v.title,
    code: v.code,
    builtin: true,
    updatedAt: 0,
  }));
}

/** Merge built-ins with the user store (user entries override built-ins by id). */
export function loadLibrary(): PineScript[] {
  const store = readUserStore();
  const byId = new Map<string, PineScript>();

  for (const s of builtinList()) byId.set(s.id, s);

  for (const [id, u] of Object.entries(store)) {
    if (u.deleted) {
      // built-in hidden, or user script removed
      if (byId.get(id)?.builtin) byId.delete(id);
      continue;
    }
    const existing = byId.get(id);
    byId.set(id, {
      id,
      title: u.title,
      code: u.code,
      builtin: existing?.builtin ?? false,
      updatedAt: u.updatedAt || 0,
      symbols: u.symbols?.length ? u.symbols.map(canonSym) : undefined,
    });
  }

  return Array.from(byId.values()).sort((a, b) => {
    // built-ins first, then most-recently-edited
    if (a.builtin !== b.builtin) return a.builtin ? -1 : 1;
    return b.updatedAt - a.updatedAt;
  });
}

/**
 * Library view for one chart symbol: scripts tagged for it first (newest
 * edit wins), then generics. Scripts tagged ONLY for other symbols drop out.
 */
export function scriptsForSymbol(lib: PineScript[], symbol: string | null | undefined): PineScript[] {
  const sym = canonSym(symbol);
  if (!sym) return lib;
  const tagged = lib.filter(s => s.symbols?.includes(sym));
  const generic = lib.filter(s => !s.symbols?.length);
  return [...tagged, ...generic];
}

/** Create / overwrite a script entry. `symbols` omitted → keep the stored tag. */
export function saveScript(id: string, title: string, code: string, symbols?: string[]): PineScript {
  const store = readUserStore();
  const updatedAt = Date.now();
  const nextSymbols = symbols !== undefined
    ? (symbols.map(canonSym).filter(Boolean).length ? symbols.map(canonSym).filter(Boolean) : undefined)
    : store[id]?.symbols;
  store[id] = { title, code, updatedAt, ...(nextSymbols?.length ? { symbols: nextSymbols } : {}) };
  writeUserStore(store);
  const builtin = id in PINE_SCRIPTS;
  return { id, title, code, builtin, updatedAt, symbols: nextSymbols };
}

/** Generate a fresh unique id for a new user script. */
export function newScriptId(): string {
  return `user_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

/** Delete a user script, or hide a built-in. Returns the updated library. */
export function deleteScript(id: string): PineScript[] {
  const store = readUserStore();
  if (id in PINE_SCRIPTS) {
    store[id] = { title: '', code: '', updatedAt: Date.now(), deleted: true };
  } else {
    delete store[id];
  }
  writeUserStore(store);
  return loadLibrary();
}

/** Reset a built-in back to its shipped template (drops the user override). */
export function resetBuiltin(id: string): PineScript | null {
  if (!(id in PINE_SCRIPTS)) return null;
  const store = readUserStore();
  delete store[id];
  writeUserStore(store);
  const v = PINE_SCRIPTS[id];
  return { id, title: v.title, code: v.code, builtin: true, updatedAt: 0 };
}

// ── per-symbol ACTIVE-SCRIPT memory — which script each symbol was last
//    editing (the "symbol-wise state preservation" the editor keys off).
const LS_ACTIVE = 'jc_pine_active_by_symbol_v1';

type ActiveMap = Record<string, string>;   // canonical symbol → script id ('' key = no-symbol context)

function readActiveMap(): ActiveMap {
  try {
    const raw = localStorage.getItem(LS_ACTIVE);
    return raw ? (JSON.parse(raw) as ActiveMap) : {};
  } catch {
    return {};
  }
}

export function rememberActiveScript(symbol: string | null | undefined, scriptId: string): void {
  try {
    const map = readActiveMap();
    map[canonSym(symbol)] = scriptId;
    localStorage.setItem(LS_ACTIVE, JSON.stringify(map));
  } catch { /* non-fatal */ }
}

/** Last script this symbol was editing, if it still exists in the library. */
export function recallActiveScript(symbol: string | null | undefined, lib: PineScript[]): PineScript | null {
  const id = readActiveMap()[canonSym(symbol)];
  return (id && lib.find(s => s.id === id)) || null;
}
